/**
 * SearchPage Component v2
 * 
 * Premium search experience with:
 * - Unified search with relevance scoring
 * - Tab filtering (All / Kinks / Sex Acts)
 * - Recent searches in localStorage
 * - Popular items when empty
 * - Keyboard navigation (arrows + enter)
 * - Match highlighting
 * - Smooth animations
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { stripMarkdown } from '../utils/stripMarkdown';
import { Search, ArrowLeft, Sparkles, Clock, X, ArrowRight, Flame, Heart, MessageSquare, Users } from 'lucide-react';
import { Spinner } from '../components/Animations';
import api from '../services/api';
import { filterVisibleKinks } from '../utils/kinkVisibility.js';

// Recent searches storage
const RECENT_KEY = 'pleasance_recent_searches';
const MAX_RECENT = 8;

function getRecentSearches() {
    try {
        return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch {
        return [];
    }
}

function addRecentSearch(term) {
    if (!term.trim()) return;
    const recent = getRecentSearches().filter(t => t.toLowerCase() !== term.toLowerCase());
    recent.unshift(term.trim());
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

function clearRecentSearches() {
    localStorage.removeItem(RECENT_KEY);
}

// Render highlighted text (markdown bold to JSX)
function HighlightedText({ text }) {
    if (!text) return null;
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return (
        <>
            {parts.map((part, i) =>
                i % 2 === 1 ? (
                    <mark key={i} className="bg-wine-500/30 text-wine-200 px-0.5 rounded">{part}</mark>
                ) : (
                    <span key={i}>{part}</span>
                )
            )}
        </>
    );
}

// Reusable Result Card Component
function ResultCard({ item, selected, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`w-full text-left bg-charcoal-950 border rounded-lg p-4 transition-all group ${selected
                ? 'border-wine-500/60 bg-charcoal-900 ring-1 ring-wine-500/30'
                : 'border-wine-900/20 hover:border-wine-500/40 hover:bg-charcoal-900'
                }`}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h3 className={`font-semibold transition-colors ${selected ? 'text-wine-400' : 'text-gray-100 group-hover:text-wine-400'
                            }`}>
                            <HighlightedText text={item.name} />
                        </h3>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${item.type === 'kink'
                            ? 'bg-wine-500/10 text-wine-400'
                            : item.type === 'archetype'
                                ? 'bg-purple-500/10 text-purple-400'
                                : 'bg-amber-500/10 text-amber-400'
                            }`}>
                            {item.type === 'kink' ? 'Kink' : item.type === 'archetype' ? 'Archetype' : 'Sex Act'}
                        </span>
                    </div>
                    {(item.summary || item.description) && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-3">
                            <HighlightedText text={stripMarkdown(item.summary || item.description)} />
                        </p>
                    )}
                </div>
                {item.category && (
                    <span className="flex-shrink-0 text-xs px-2 py-1 bg-dark-700/50 text-gray-400 rounded">
                        {item.category}
                    </span>
                )}
            </div>
        </button>
    );
}

function SearchPage() {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('all'); // 'all' | 'kinks' | 'actions' | 'archetypes'
    const [results, setResults] = useState({ kinks: [], actions: [], archetypes: [], totalKinks: 0, totalActions: 0, totalArchetypes: 0 });
    const [popular, setPopular] = useState([]);
    const [recentSearches, setRecentSearches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const resultsRef = useRef(null);

    // Load recent searches and popular on mount
    useEffect(() => {
        setRecentSearches(getRecentSearches());

        // Fetch popular items for empty state
        const apiBase = import.meta.env.VITE_API_URL
            ? `${import.meta.env.VITE_API_URL}/api`
            : '/api';
        fetch(`${apiBase}/search/popular?limit=12`)
            .then(res => res.json())
            .then(data => setPopular(data.popular || []))
            .catch(() => setPopular([]));
    }, []);

    // Auto-focus search input
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Perform search
    const handleSearch = useCallback(async () => {
        if (!searchTerm.trim()) {
            setResults({ kinks: [], actions: [], archetypes: [], totalKinks: 0, totalActions: 0, totalArchetypes: 0 });
            setSelectedIndex(-1);
            return;
        }

        setLoading(true);
        try {
            const data = await api.search(searchTerm, activeTab, 30);
            setResults({
                kinks: data.kinks || [],
                actions: data.actions || [],
                archetypes: data.archetypes || [],
                totalKinks: data.totalKinks || 0,
                totalActions: data.totalActions || 0,
                totalArchetypes: data.totalArchetypes || 0
            });
            setSelectedIndex(-1);
        } catch (err) {
            console.error('Search failed:', err);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, activeTab]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(handleSearch, 250);
        return () => clearTimeout(timer);
    }, [handleSearch]);

    const visibleKinkResults = filterVisibleKinks(results.kinks || []);

    // For specific tabs, we just return the full list
    // For 'all' tab, we handle rendering differently directly in the JSX
    const getTabResults = () => {
        if (activeTab === 'kinks') return visibleKinkResults;
        if (activeTab === 'actions') return results.actions;
        if (activeTab === 'archetypes') return results.archetypes;
        return [];
    };

    const specificTabResults = getTabResults();
    const visiblePopular = [
        ...filterVisibleKinks(popular.filter((item) => item.type === 'kink')),
        ...popular.filter((item) => item.type !== 'kink')
    ];

    // Keyboard navigation
    // Keyboard navigation needs to operate on a flattened list of whatever is currently visible
    // For simplicity in 'all' view, we might disable keyboard nav or just flatten the top 5 of each for navigation
    const getNavigableItems = () => {
        if (activeTab !== 'all') return specificTabResults;
        return [
            ...visibleKinkResults.slice(0, 5),
            ...results.actions.slice(0, 5),
            ...results.archetypes.slice(0, 5)
        ];
    };

    const navigableItems = getNavigableItems();

    const handleKeyDown = (e) => {
        if (!searchTerm.trim() || navigableItems.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => Math.min(prev + 1, navigableItems.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => Math.max(prev - 1, -1));
                break;
            case 'Enter':
                if (selectedIndex >= 0 && selectedIndex < navigableItems.length) {
                    e.preventDefault();
                    navigateToResult(navigableItems[selectedIndex]);
                }
                break;
            case 'Escape':
                setSearchTerm('');
                setSelectedIndex(-1);
                break;
        }
    };

    // Navigate to result and save to recent
    const navigateToResult = (item) => {
        addRecentSearch(searchTerm);
        const path = item.type === 'kink' ? `/kink/${item.id}`
            : item.type === 'archetype' ? `/archetype/${item.id}`
                : `/action/${item.id}`;
        navigate(path);
    };

    // Use recent search
    const useRecentSearch = (term) => {
        setSearchTerm(term);
        inputRef.current?.focus();
    };

    // Tab counts
    const tabCounts = {
        all: visibleKinkResults.length + results.totalActions + results.totalArchetypes,
        kinks: visibleKinkResults.length,
        actions: results.totalActions,
        archetypes: results.totalArchetypes
    };

    return (
        <div className="min-h-screen bg-charcoal py-8">
            <div className="max-w-4xl mx-auto px-6">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="group flex items-center gap-2 px-4 py-2 rounded-lg bg-dark-800/80 hover:bg-dark-700 border border-dark-700 hover:border-wine-500/30 transition-all text-gray-400 hover:text-white mb-6"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                        <span className="font-medium text-sm">Back</span>
                    </button>
                    <h1 className="font-display text-4xl md:text-5xl font-bold text-gray-100 mb-3 tracking-tight">Search</h1>
                    <p className="text-gray-400 text-lg">Explore 1,100+ kinks and 200+ techniques</p>
                </div>

                {/* Search Input */}
                <div className="relative mb-6 group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-wine-500 to-amber-500 rounded-lg blur opacity-20 group-focus-within:opacity-75 transition duration-500"></div>
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Search for kinks, sex acts, or practices..."
                        className="relative w-full bg-dark-900 border border-dark-700 rounded-lg px-14 py-4 text-gray-100 text-lg placeholder-gray-500 focus:outline-none focus:ring-0"
                    />
                    <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-500 group-focus-within:text-wine-500 transition-colors z-10" />
                    {searchTerm && (
                        <button
                            onClick={() => { setSearchTerm(''); inputRef.current?.focus(); }}
                            className="absolute right-14 top-1/2 -translate-y-1/2 z-10 p-1 rounded-full hover:bg-dark-700 text-gray-500 hover:text-gray-300 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    )}
                    {loading && (
                        <div className="absolute right-5 top-1/2 -translate-y-1/2 z-10">
                            <Spinner size="sm" />
                        </div>
                    )}
                </div>

                {/* Ask The Guide Button */}
                <div className="mb-6">
                    <button
                        onClick={() => navigate(searchTerm.trim() ? `/chat?q=${encodeURIComponent(`Tell me about: ${searchTerm}`)}` : '/chat')}
                        className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-wine-500/10 to-amber-500/10 hover:from-wine-500/20 hover:to-amber-500/20 border border-wine-500/20 hover:border-wine-500/40 text-gray-200 transition-all group"
                    >
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-wine-500 to-amber-500 flex items-center justify-center">
                            <span className="text-base">🕯️</span>
                        </div>
                        <div className="text-left flex-1">
                            <div className="font-medium">
                                {searchTerm.trim() ? `Ask The Guide about "${searchTerm.slice(0, 30)}${searchTerm.length > 30 ? '...' : ''}"` : 'Ask The Guide'}
                            </div>
                            <div className="text-xs text-gray-500">Get personalized answers about kinks & intimacy</div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-500 group-hover:text-wine-400 group-hover:translate-x-1 transition-all" />
                    </button>
                </div>

                {/* Tabs - Only show when we have search results */}
                {searchTerm.trim() && (visibleKinkResults.length > 0 || results.totalActions > 0) && (
                    <div className="flex gap-2 mb-6">
                        {[
                            { id: 'all', label: 'All', icon: Sparkles },
                            { id: 'kinks', label: 'Kinks', icon: Heart },
                            { id: 'actions', label: 'Sex Acts', icon: Flame },
                            { id: 'archetypes', label: 'Archetypes', icon: Users }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id
                                    ? 'bg-wine-500/20 text-wine-400 border border-wine-500/30'
                                    : 'bg-dark-800/50 text-gray-400 border border-transparent hover:border-dark-600 hover:text-gray-300'
                                    }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                                {searchTerm.trim() && tabCounts[tab.id] > 0 && (
                                    <span className="ml-1 px-1.5 py-0.5 bg-dark-700/50 rounded text-xs">
                                        {tabCounts[tab.id]}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                {/* Results */}
                {searchTerm.trim() ? (
                    <div ref={resultsRef} className="space-y-8">
                        {activeTab === 'all' ? (
                            <>
                                {/* Kinks Section */}
                                {visibleKinkResults.length > 0 && (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-lg font-bold text-gray-200 flex items-center gap-2">
                                                <Heart className="w-5 h-5 text-wine-500" /> Kinks
                                                <span className="bg-dark-700/50 text-gray-400 text-xs px-2 py-0.5 rounded-full">{visibleKinkResults.length}</span>
                                            </h3>
                                            {visibleKinkResults.length > 5 && (
                                                <button
                                                    onClick={() => setActiveTab('kinks')}
                                                    className="text-sm text-wine-400 hover:text-wine-300 transition-colors"
                                                >
                                                    View all {visibleKinkResults.length}
                                                </button>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            {visibleKinkResults.slice(0, 5).map((item, index) => (
                                                <ResultCard
                                                    key={`kink-${item.id}`}
                                                    item={item}
                                                    selected={selectedIndex === index}
                                                    onClick={() => navigateToResult(item)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Sex Actions Section */}
                                {results.actions.length > 0 && (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-lg font-bold text-gray-200 flex items-center gap-2">
                                                <Flame className="w-5 h-5 text-amber-500" /> Sex Acts
                                                <span className="bg-dark-700/50 text-gray-400 text-xs px-2 py-0.5 rounded-full">{results.totalActions}</span>
                                            </h3>
                                            {results.totalActions > 5 && (
                                                <button
                                                    onClick={() => setActiveTab('actions')}
                                                    className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
                                                >
                                                    View all {results.totalActions}
                                                </button>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            {results.actions.slice(0, 5).map((item, index) => {
                                                // Adjust index for keyboard nav continuity
                                                const navIndex = visibleKinkResults.slice(0, 5).length + index;
                                                return (
                                                    <ResultCard
                                                        key={`action-${item.id}`}
                                                        item={item}
                                                        selected={selectedIndex === navIndex}
                                                        onClick={() => navigateToResult(item)}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Archetypes Section */}
                                {results.archetypes.length > 0 && (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-lg font-bold text-gray-200 flex items-center gap-2">
                                                <Users className="w-5 h-5 text-purple-500" /> Archetypes
                                                <span className="bg-dark-700/50 text-gray-400 text-xs px-2 py-0.5 rounded-full">{results.totalArchetypes}</span>
                                            </h3>
                                            {results.totalArchetypes > 5 && (
                                                <button
                                                    onClick={() => setActiveTab('archetypes')}
                                                    className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                                                >
                                                    View all {results.totalArchetypes}
                                                </button>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            {results.archetypes.slice(0, 5).map((item, index) => {
                                                // Adjust index for keyboard nav
                                                const navIndex = visibleKinkResults.slice(0, 5).length + results.actions.slice(0, 5).length + index;
                                                return (
                                                    <ResultCard
                                                        key={`archetype-${item.id}`}
                                                        item={item}
                                                        selected={selectedIndex === navIndex}
                                                        onClick={() => navigateToResult(item)}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {visibleKinkResults.length === 0 && results.totalActions === 0 && results.totalArchetypes === 0 && !loading && (
                                    <div className="text-center py-12">
                                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-wine-500/10 flex items-center justify-center">
                                            <Search className="w-8 h-8 text-wine-500/40" />
                                        </div>
                                        <p className="text-gray-400">No results found for "{searchTerm}"</p>
                                        <p className="text-sm text-gray-500 mt-1">Try different keywords</p>
                                    </div>
                                )}
                            </>
                        ) : (
                            /* Specific Tab View */
                            <div className="space-y-2">
                                {specificTabResults.length > 0 ? (
                                    specificTabResults.map((item, index) => (
                                        <ResultCard
                                            key={`${item.type}-${item.id}`}
                                            item={item}
                                            selected={selectedIndex === index}
                                            onClick={() => navigateToResult(item)}
                                        />
                                    ))
                                ) : !loading && (
                                    <div className="text-center py-12">
                                        <p className="text-gray-400">No {activeTab} found for "{searchTerm}"</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Keyboard hint */}
                        {(results.totalKinks > 0 || results.totalActions > 0 || results.totalArchetypes > 0) && (
                            <p className="text-center text-xs text-gray-600 mt-4">
                                Use <kbd className="px-1.5 py-0.5 bg-dark-700 rounded text-gray-400">↑</kbd> <kbd className="px-1.5 py-0.5 bg-dark-700 rounded text-gray-400">↓</kbd> to navigate, <kbd className="px-1.5 py-0.5 bg-dark-700 rounded text-gray-400">Enter</kbd> to select
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="space-y-8">
                        {/* Recent Searches */}
                        {recentSearches.length > 0 && (
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="flex items-center gap-2 text-sm font-medium text-gray-400">
                                        <Clock className="w-4 h-4" />
                                        Recent Searches
                                    </h2>
                                    <button
                                        onClick={() => { clearRecentSearches(); setRecentSearches([]); }}
                                        className="text-xs text-gray-500 hover:text-gray-400 transition-colors"
                                    >
                                        Clear
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {recentSearches.map((term, i) => (
                                        <button
                                            key={i}
                                            onClick={() => useRecentSearch(term)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-dark-800/50 hover:bg-dark-700 border border-dark-700 hover:border-wine-500/30 rounded-lg text-sm text-gray-400 hover:text-gray-200 transition-all"
                                        >
                                            {term}
                                            <ArrowRight className="w-3 h-3 opacity-50" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Popular / Explore */}
                        {visiblePopular.length > 0 && (
                            <div>
                                <h2 className="flex items-center gap-2 text-sm font-medium text-gray-400 mb-4">
                                    <Sparkles className="w-4 h-4" />
                                    Explore
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {visiblePopular.slice(0, 8).map((item) => (
                                        <button
                                            key={`${item.type}-${item.id}`}
                                            onClick={() => navigateToResult(item)}
                                            className="text-left bg-charcoal-950 border border-wine-900/20 rounded-lg p-4 hover:border-wine-500/30 hover:bg-charcoal-900 transition-all group"
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${item.type === 'kink'
                                                    ? 'bg-wine-500/10 text-wine-400'
                                                    : 'bg-amber-500/10 text-amber-400'
                                                    }`}>
                                                    {item.type === 'kink' ? <Heart className="w-4 h-4" /> : <Flame className="w-4 h-4" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-medium text-gray-100 group-hover:text-wine-400 transition-colors truncate">
                                                        {item.name}
                                                    </h3>
                                                    {(item.summary || item.description) && (
                                                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                                                            {stripMarkdown(item.summary || item.description)}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Empty state fallback */}
                        {recentSearches.length === 0 && visiblePopular.length === 0 && (
                            <div className="text-center py-12">
                                <Search className="w-16 h-16 mx-auto mb-4 text-gray-700" />
                                <p className="text-gray-500">Start typing to search kinks and sex acts</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default SearchPage;
